// The trophy-points milestone ladder.
//
// Points are seconds saved against a target, and they reset at midnight, so
// these numbers are a day's work. The rungs were first set from the real
// distribution (2,794 operator-days to 2026-08-20: median 1,029, p90 5,103,
// best ever 12,192) — a ladder topping out at 3,000 had fired the full-screen
// fireworks up to twelve times PER OPERATOR PER DAY, which turns the best
// moment of someone's day into wallpaper.
//
// RE-TUNED 2026-08-26 for the target rebalance (migrations 169/171, the
// ~2,000 pts/engaged-hr anchor): the underpaid flows (picking, drops,
// sorter 2, P&S) now pay 2-6x what they did, so the floor-wide median day
// projects to roughly 1,500 and the old rungs would have made tier 3 a daily
// event. Projected hit rates at these rungs (RE-DERIVE from live data at the
// ~2026-09-09 ladder check — scratch/points-ladder-verify.mjs):
//
//     500 → ~70%       5,000 → ~18%
//   1,000 → ~60%       7,500 →  ~7%   (full screen + fanfare starts here)
//   2,500 → ~38%      10,000 →  ~2%   (gold — 10k in a shift is the summit:
//                                      ~5 fully-engaged hours at record pace)

export const LADDER = [500, 1000, 2500, 5000, 7500, 10000] as const;

// Past the top of the ladder the top tier repeats on this step, so a monster
// day keeps being recognised instead of going quiet. 5,000 rather than 1,000:
// the point of the top rung is that reaching it again is a real achievement.
export const TOP_STEP = 5000;

export type MilestoneTier = 1 | 2 | 3;

export interface Milestone {
    /** The milestone just reached, e.g. 1000. */
    value: number;
    tier: MilestoneTier;
}

/** How long the celebration owns the screen. Bigger milestone, longer moment. */
export const TIER_DURATION_MS: Record<MilestoneTier, number> = {
    1: 1700,
    2: 2300,
    3: 3400,
};

export const tierFor = (value: number): MilestoneTier =>
    value >= 7500 ? 3 : value >= 2500 ? 2 : 1;

/** Gold treatment for the top rung and above — roughly 1% of operator-days. */
export const isGrandMilestone = (value: number): boolean => value >= 10000;

// The highest milestone at or below n, or null below the first rung.
const highestAtOrBelow = (n: number): number | null => {
    if (!Number.isFinite(n) || n < LADDER[0]) return null;
    const top = LADDER[LADDER.length - 1];
    if (n >= top) return Math.floor(n / TOP_STEP) * TOP_STEP;
    let best: number | null = null;
    for (const rung of LADDER) {
        if (rung <= n) best = rung;
    }
    return best;
};

/**
 * The milestone crossed by moving from `prev` to `next`, or null.
 *
 * A single award can vault more than one rung — 240 → 600 clears both 250 and
 * 500. Only the highest fires: two celebrations stacked on one scan would
 * collide in the same overlay slot, and the smaller one is the less
 * interesting fact.
 */
export const crossedMilestone = (prev: number, next: number): Milestone | null => {
    if (!Number.isFinite(prev) || !Number.isFinite(next)) return null;
    if (next <= prev) return null;
    const value = highestAtOrBelow(next);
    if (value === null || value <= prev) return null;
    return { value, tier: tierFor(value) };
};
